import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { auth, db } from '../firebase.js'; // 1. Import Firebase (not Supabase)
import { signOut } from 'firebase/auth'; // 2. Import Firebase signOut
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore'; // 3. Import Firestore functions
import './UserDashboard.css'; 
import logoLight from '../assests/logo-light.png'; 

// --- Icon Components ---
const IconClock = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);
const IconTruck = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);
const IconSkip = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 18H5.01" /><path d="M19 6H19.01" /><path d="M12 12L2 22" /><path d="M22 2L12 12" />
  </svg>
);
// --- End Icon Components ---

// --- Header for the Dashboard ---
const UserHeader = ({ user, onLogout }) => (
  <header className="user-header">
    <div className="user-header-container">
      <div className="logo-container">
        <img src={logoLight} alt="Degchi Logo" className="logo-img" />
        
      </div>
      <div className="header-user-info">
        <span>Welcome, {user?.name || user?.email}!</span>
        <button onClick={onLogout} className="user-logout-button">Logout</button>
      </div>
    </div>
  </header>
);

// --- Dashboard Cards ---
const DeliveryCard = ({ status, eta }) => {
  const statusInfo = {
    'Pending': { text: 'We are preparing your meal.', icon: <IconClock />, color: 'amber' },
    'Out for Delivery': { text: 'Your dinner is on its way!', icon: <IconTruck />, color: 'blue' },
    'Delivered': { text: 'Enjoy your meal! Delivered at', icon: <IconCheck />, color: 'emerald' },
    'On Hold': { text: 'Your subscription is paused.', icon: null, color: 'slate' },
  };
  const current = statusInfo[status] || statusInfo['Pending'];

  return (
    <div className={`user-card delivery-card ${current.color}`}>
      <h3>Today's Delivery</h3>
      <div className="delivery-status-content">
        <div className="delivery-icon">{current.icon}</div>
        <div>
          <p className="delivery-status-text">{status}</p>
          <p className="delivery-eta">{current.text} {status !== 'On Hold' && <strong>{eta}</strong>}</p>
        </div>
      </div>
    </div>
  );
};

const SubscriptionCard = ({ plan, mealsLeft, totalMeals }) => {
  const percentage = (mealsLeft / totalMeals) * 100;
  return (
    <div className="user-card">
      <h3>Your Subscription</h3>
      <p className="plan-name">{plan}</p>
      <div className="meal-counter">
        <span className="meal-count">{mealsLeft}</span>
        <span className="meal-total">/ {totalMeals} Meals Remaining</span>
      </div>
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${percentage}%` }}></div>
      </div>
    </div>
  );
};

const HistoryCard = ({ skippedMeals }) => (
  <div className="user-card">
    <h3>Meal History</h3>
    <div className="history-content">
      <div className="history-icon"><IconSkip /></div>
      <div>
        <p className="history-count">{skippedMeals}</p>
        <p className="history-text">Meals credited back</p>
      </div>
    </div>
  </div>
);

// --- Main UserDashboard Component ---
function UserDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the user's specific subscription data from Firestore
  useEffect(() => {
    if (currentUser) {
      const fetchSubscription = async () => {
        try {
          // 4. Use getDoc to find the subscription data
          // This assumes data is in a "subscriptions" collection
          const subDoc = await getDoc(doc(db, "subscriptions", currentUser.uid));
          if (subDoc.exists()) {
            setSubscription(subDoc.data());
          } else {
            // No subscription found, set some defaults
            setSubscription({ plan: 'No Plan Active', mealsLeft: 0, totalMeals: 0, skippedMeals: 0, deliveryStatus: 'On Hold', deliveryETA: 'N/A' });
          }
        } catch (error) {
          console.error("Error fetching subscription:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchSubscription();
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      // 5. Use Firebase signOut
      await signOut(auth);
      navigate('/'); // Go back to homepage after logout
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  if (loading || !subscription) {
    return <div style={{padding: '2rem'}}>Loading your dashboard...</div>;
  }

  return (
    <div className="user-dashboard-layout">
      <UserHeader user={currentUser} onLogout={handleLogout} />
      <main className="user-dashboard-content">
        <div className="dashboard-grid">
          <div className="grid-col-span-2">
            <DeliveryCard status={subscription.deliveryStatus} eta={subscription.deliveryETA} />
          </div>
          <div className="grid-col-span-1">
            <SubscriptionCard 
              plan={subscription.plan} 
              mealsLeft={subscription.mealsLeft} 
              totalMeals={subscription.totalMeals} 
            />
          </div>
          <div className="grid-col-span-1">
            <HistoryCard skippedMeals={subscription.skippedMeals} />
          </div>
        </div>
      </main>
    </div>
  );
}
export default UserDashboard;

